import { WifiOff, RefreshCw, DownloadCloud } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * Persistent sticky header showing online/offline state and sync queue count.
 * Follows DESIGN.md §5.2 — green dot online, amber banner offline.
 * Clicking the pending sync counter redirects to /sync-hub.
 */
export function StatusBar() {
  const { isOnline, pendingSyncCount, isSyncing } = useOfflineSync();
  const { isInstallable, installPWA } = usePWAInstall();

  return (
    <header className="sticky top-0 z-50 w-full">
      {isOnline ? (
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-divider">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-synced opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-synced"></span>
            </span>
            <span className="text-sm font-medium text-graphite font-secondary">Online</span>
          </div>
          <div className="flex items-center gap-4">
            {isInstallable && (
              <button
                onClick={installPWA}
                className="flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded-full transition-colors"
              >
                <DownloadCloud size={14} />
                Install App
              </button>
            )}
            {pendingSyncCount > 0 && (
              <Link
                to="/sync-hub"
                className="flex items-center gap-2 text-sm text-offline font-medium hover:underline"
              >
                <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                <span>
                  [{pendingSyncCount}] pending edits
                </span>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b-2 border-offline">
          <div className="flex items-center gap-2">
            <WifiOff size={18} className="text-amber-700" />
            <span className="text-sm font-semibold text-amber-800 font-secondary">
              Offline Mode. Local Save Active.
            </span>
          </div>
          {pendingSyncCount > 0 && (
            <Link
              to="/sync-hub"
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-200 text-amber-900 hover:bg-amber-300 transition-colors"
            >
              [{pendingSyncCount}] pending
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
